import { useState } from 'react';
import { Delete, Keyboard } from 'lucide-react';
import { SMILEYS, smileyUrl } from '../lib/smileys.ts';
import { useSmileyStats } from '../lib/smileyStats.ts';
import './SmileyPanel.css';

type Category = 'Часто' | 'Все';

const CATEGORIES: Category[] = ['Часто', 'Все'];

const HINTS: Record<Category, string> = {
  Часто: 'Часто — по вашей статистике',
  Все: 'Все смайлы',
};

/**
 * The phone's smiley panel: it takes the keyboard's place rather than floating
 * over the thread, so it is a plain block of a fixed height rather than the
 * popover the desktop uses (SmileyPicker). Its height is decided by whoever
 * mounts it — the composer, which knows how tall the keyboard was.
 *
 * In reaction mode it is the same grid picking a reaction for a message instead
 * of inserting into the input, which is why the banner and the backspace key
 * are the only things that change.
 */
export function SmileyPanel({
  height,
  reacting,
  onPick,
  onKeyboard,
  onBackspace,
  onCancelReaction,
}: {
  height: number;
  /** Picking a reaction for a message rather than typing one into a message. */
  reacting: boolean;
  onPick: (slug: string) => void;
  onKeyboard: () => void;
  onBackspace: () => void;
  onCancelReaction: () => void;
}) {
  const [category, setCategory] = useState<Category>('Часто');
  const frequent = useSmileyStats();
  const shown = category === 'Часто' ? frequent : SMILEYS;

  return (
    <div className="smiley-slot" style={{ height }} role="group" aria-label="Смайлы">
      {reacting && (
        <div className="smiley-slot-banner">
          <span>Выберите реакцию</span>
          <button type="button" className="smiley-slot-cancel" onClick={onCancelReaction}>
            Отмена
          </button>
        </div>
      )}

      <div className="smiley-slot-cats">
        {CATEGORIES.map((name) => (
          <button
            key={name}
            type="button"
            className={`smiley-slot-cat${category === name ? ' smiley-slot-cat-active' : ''}`}
            aria-pressed={category === name}
            onClick={() => setCategory(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="smiley-slot-grid">
        {shown.map((smiley) => (
          <button
            key={smiley.slug}
            type="button"
            className="smiley-slot-cell"
            aria-label={smiley.label}
            onClick={() => onPick(smiley.slug)}
          >
            <img src={smileyUrl(smiley.slug)} alt="" loading="lazy" />
          </button>
        ))}
      </div>

      <div className="smiley-slot-foot">
        <button
          type="button"
          className="smiley-slot-key"
          onClick={onKeyboard}
          aria-label="Клавиатура"
        >
          <Keyboard size={18} />
        </button>
        <span className="smiley-slot-hint">{HINTS[category]}</span>
        {/* Reaction mode has no message being typed, so there is nothing to
            rub out — the slot stays occupied to keep the row from shifting. */}
        {reacting ? (
          <span className="smiley-slot-key smiley-slot-key-empty" />
        ) : (
          <button
            type="button"
            className="smiley-slot-key"
            onClick={onBackspace}
            aria-label="Стереть"
          >
            <Delete size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
